// Did this line actually get what the option ASKED for?
//
// An option is created against a material selection basis ("GAF via ABC Supply"), but the basis is
// a preference, not a guarantee: no supplier carries every nail, membrane and flashing on a roof,
// so `resolve_price` falls back rather than failing the line (see app/domain/estimates/pricing.py).
// That fallback is invisible on screen — the row simply shows whichever supplier ended up pricing
// it — which is exactly the silent substitution a quote must not make without saying so.
//
// Three states, because there are three different things to do about them:
//   MATCHED      the basis held; nothing to do
//   SUBSTITUTED  someone else priced it; fine, but it is a deviation the sender should know about
//   UNAVAILABLE  nothing priced it at all; the line is not quotable as it stands
import type { EstimateLine, GenerationMethod, MaterialItemDetail } from '@/redux/internal-services/estimatesApi';

export type BasisConformance = 'MATCHED' | 'SUBSTITUTED' | 'UNAVAILABLE';

/** Tailwind classes per state. Status tokens flip for dark mode on their own, so no per-component
 *  `dark:` variant belongs here. */
export const CONFORMANCE_CLASS: Record<BasisConformance, string> = {
  MATCHED: 'bg-success/10 text-success-text',
  SUBSTITUTED: 'bg-warning/10 text-warning-text',
  UNAVAILABLE: 'bg-danger/10 text-danger-text',
};

/** Just the dot — used by the section heading's roll-up ("4 matched, 1 substituted"), where a
 *  count needs a colour key and there is no row to tint. */
export const CONFORMANCE_DOT: Record<BasisConformance, string> = {
  MATCHED: 'bg-success',
  SUBSTITUTED: 'bg-warning',
  UNAVAILABLE: 'bg-danger',
};

/** The collapsed row's own tint. Replaces an 8px dot: at that size the amber and green read as
 *  the same smudge until you lean in, and finding the one substituted line meant scanning a column
 *  of dots. Tinting the whole row makes the odd one out visible at a glance.
 *
 *  A 10% status tint — this sits behind the line's own text, so it has to stay a wash rather than
 *  a colour. The status tokens flip with the theme, which is why there is no `dark:` variant here. */
export const CONFORMANCE_ROW: Record<BasisConformance, string> = {
  MATCHED: 'bg-success/10',
  SUBSTITUTED: 'bg-warning/10',
  UNAVAILABLE: 'bg-danger/10',
};

/**
 * `null` means the basis makes no demand of this line — a SERVICE line, or a LOWEST_COST option,
 * where every supplier is equally acceptable and a green dot would be answering a question nobody
 * asked. An unpriced line still reports UNAVAILABLE under any basis: that one is true regardless
 * of what was requested.
 */
export function lineBasisConformance(
  line: EstimateLine,
  basis: GenerationMethod | null | undefined,
  detail: MaterialItemDetail | null | undefined,
): BasisConformance | null {
  const m = line.material;
  if (line.line_type !== 'MATERIAL' || !m) return null;

  // A hand-typed custom line (add_custom_estimate_material_line) has no product_variant_code by
  // design — it was never meant to resolve against the catalogue, so it isn't "unavailable", it's
  // simply outside what a basis can ask of it. Same reasoning as a SERVICE line above: null, not
  // UNAVAILABLE.
  if (m.price_source === 'MANUAL') return null;

  // Unpriced or unresolved: no variant satisfied the WBS resource, or nothing in the catalogue
  // carries a price for it. Nothing the basis says can rescue that.
  if (!m.product_variant_code || m.unit_cost === null || m.unit_cost === undefined) return 'UNAVAILABLE';

  const wantedSupplier = basis?.method_code === 'PREFERRED_SUPPLIER'
    ? basis.parameters?.supplier_code ?? null : null;
  if (wantedSupplier) {
    if (!m.supplier_code) return 'UNAVAILABLE';
    return m.supplier_code === wantedSupplier ? 'MATCHED' : 'SUBSTITUTED';
  }

  const wantedManufacturer = basis?.method_code === 'PREFERRED_MANUFACTURER'
    ? basis.parameters?.manufacturer_code ?? null : null;
  if (wantedManufacturer) {
    // The line stores only the variant code; the manufacturer comes from the item-details lookup,
    // so an unloaded lookup is "not known yet", not "wrong manufacturer".
    if (!detail) return null;
    if (!detail.manufacturer_code) return 'UNAVAILABLE';
    return detail.manufacturer_code === wantedManufacturer ? 'MATCHED' : 'SUBSTITUTED';
  }

  return null;
}

/** How many lines sit in each state — the heading roll-up, so a substitution is visible without
 *  expanding thirty rows to hunt for the one amber dot. */
export function conformanceTally(states: (BasisConformance | null)[]) {
  return {
    matched: states.filter((s) => s === 'MATCHED').length,
    substituted: states.filter((s) => s === 'SUBSTITUTED').length,
    unavailable: states.filter((s) => s === 'UNAVAILABLE').length,
  };
}
